<template>
  <div class="checkout">
    <Banners />
    <v-row justify="center">
      <v-col
        :xl="6"
        :lg="8"
        :xs="10"
      >
        <v-row>
          <v-col
            cols="12"
            sm="7"
            order-sm="last"
            order="first"
          >
            <v-card
              class="mb-3"
              outlined
            >
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-title>
                    <h2>Secure Card Payment</h2>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <font-awesome-icon class="mr-1" size="lg" :icon="['fab', 'cc-visa']" />
                    <font-awesome-icon class="mr-1" size="lg" :icon="['fab', 'cc-mastercard']" />
                    <font-awesome-icon class="mr-1" size="lg" :icon="['fab', 'cc-jcb']" />
                    <font-awesome-icon class="mr-1" size="lg" :icon="['fab', 'cc-discover']" />
                    <font-awesome-icon class="mr-1" size="lg" :icon="['fab', 'cc-diners-club']" />
                    <font-awesome-icon class="mr-1" size="lg" :icon="['fab', 'cc-amex']" />
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-card-text>
                <v-form
                  ref="form"
                  v-model="validForm"
                >
                  <PaymentForm
                    v-if="$store.getters.booking.id"
                    ref="paymentForm"
                    :stripe-total="$store.getters.booking.total * 100"
                    :booking-id="$store.getters.booking.id"
                    @changeLoading="(isLoading) => { loading = isLoading }"
                    @changeStripeComplete="(isComplete) => { stripeIsComplete = isComplete }"
                    @paymentComplete="handleCompletePayment"
                  />
                </v-form>
              </v-card-text>
            </v-card>

            <v-alert
              v-if="$store.getters.booking.total === 0"
              border="top"
              type="success"
              text
            >
              <strong>Your Payment Method Will Not Be Charged</strong>
              <p>You’re in the enviable position of not having to pay a penny today! However, we do need you to enter a payment method for us to confirm the booking. This is so we can confirm you are a real person with a real bike. Your card will not be charged without your explicit consent.</p>
            </v-alert>
          </v-col>
          <v-col
            cols="12"
            sm="5"
            order-sm="last"
            order="first"
          >
            <v-card outlined class="mb-4">
              <v-card-text>
                <h4 class="mb-4">Cancellations & Warranty Policy</h4>

                <p class="text--secondary caption"><v-icon small left color="green">mdi-check-circle</v-icon> Guaranteed collection on date booked or your service for free!</p>
                <p class="text--secondary caption"><v-icon small left color="green">mdi-check-circle</v-icon> Free cancellation / amendment up to 24 hours before collection</p>
                <p class="text--secondary caption"><v-icon small left color="green">mdi-check-circle</v-icon> 12-month parts and 3-month labour warranty on our work</p>
              </v-card-text>
            </v-card>
            <div>
              <v-alert
                v-if="$store.getters.promoIsActive('BF2021') && $store.getters.selectedServiceSkus.includes('deluxe') || $store.getters.selectedServiceSkus.includes('super_deluxe')"
                border="top"
                color="#ffec3d"
              >
                <div v-if="$store.getters.booking.discount_code_used && $store.getters.booking.discount_code_used === 'BF2021'">
                  Black Friday offer applied successfully!
                </div>
                <div v-else>
                  Enter the code <strong>BF2021</strong> below for a discount on your Deluxe or Super Deluxe service.
                </div>
              </v-alert>

              <v-btn
                v-if="!$store.getters.booking.discount"
                outlined
                x-small
                block
                color="primary"
                @click="() => showCouponField = !showCouponField">
                {{ showCouponField ? 'Clear coupon' : 'Àpply coupon / Gift Card?' }}
              </v-btn>

              <div v-if="showCouponField && !$store.getters.booking.discount">
                <v-text-field
                  v-model="form.discountCode"
                  class="mt-2"
                  label="Coupon Code"
                  background-color="white"
                  outlined
                  dense
                />

                <v-btn
                  small
                  color="primary"
                  @click="applyCoupon">
                  Apply Coupon
                </v-btn>
              </div>
            </div>

            <v-card outlined class="mt-4">
              <v-simple-table>
                <template v-slot:default>
                  <tbody>
                    <tr
                      v-for="bike in $store.getters.booking.bikes"
                      :key="bike.id"
                    >
                      <td>{{ bike.service.name }} <small v-if="bike.manafacturer">({{ bike.manafacturer }} <span v-if="bike.model">{{ bike.model }}</span>)</small></td>
                      <td v-if="bike.total">£{{ bike.total.toFixed(2) }}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td></td>
                    </tr>
                    <tr>
                      <td>Sub Total</td>
                      <td v-if="$store.getters.booking.subtotal">£{{ $store.getters.booking.subtotal.toFixed(2) }}</td>
                    </tr>
                    <tr>
                      <td>VAT (Inclusive)</td>
                      <td v-if="$store.getters.booking.tax !== null">£{{ $store.getters.booking.tax.toFixed(2) }}</td>
                    </tr>
                    <tr v-if="$store.getters.booking.discount">
                      <td>Discount</td>
                      <td v-if="$store.getters.booking.discount">£{{ $store.getters.booking.discount.toFixed(2) }} <small>({{ $store.getters.booking.discount_code_description }})</small></td>
                    </tr>
                    <tr>
                      <td><strong>Total</strong></td>
                      <td v-if="$store.getters.booking.total !== null"><strong>£{{ $store.getters.booking.total.toFixed(2) }}</strong></td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card>

            <div class="mt-4">
              <v-btn
                v-if="$store.getters.booking.total !== null"
                :loading="loading"
                color="primary"
                block
                x-large
                @click="handlePayment">
                {{ $store.getters.booking.total ? `Pay £${$store.getters.booking.total.toFixed(2)}` : 'Confirm Booking' }}<v-icon right dark>mdi-chevron-right</v-icon>
              </v-btn>

              <v-btn
                v-if="$store.getters.bookingForm.bikes.length > 0"
                :loading="loading"
                class="mt-4"
                block
                small
                text
                @click="$router.push({ name: 'pageThree' })">
                Back
              </v-btn>
            </div>

            <v-snackbar
              v-model="showError"
              color="error"
              bottom
            >
              {{ error }}
              <v-btn
                dark
                text
                @click="showError = false"
              >
                Close
              </v-btn>
            </v-snackbar>

            <v-snackbar
              v-model="showMessage"
              bottom
            >
              {{ message }}
              <v-btn
                dark
                text
                @click="showError = false"
              >
                Close
              </v-btn>
            </v-snackbar>

          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import PaymentForm from '@/components/PaymentForm'
import Banners from '@/components/Banners'
import axios from 'axios'

export default {
  name: 'PageFour',

  props: {
    bookingId: {
      type: [String, Number],
      required: false,
      default: () => { return null }
    }
  },

  components: {
    PaymentForm,
    Banners
  },

  beforeRouteEnter (to, from, next) {
    next(vm => {
      var existingBooking = vm.$route.params.bookingId && vm.$route.query.signature
      if (vm.$store.getters.bookingForm.bikes.length === 0 && !existingBooking) {
        vm.$router.push({ name: 'pageOne' })
      }

      if (existingBooking) {
        const axiosInstance = axios.create({
          baseURL: process.env.VUE_APP_API_BASE_URL
        })
        axiosInstance.defaults.headers.common['Client-Id'] = process.env.VUE_APP_CLIENT_ID
        axiosInstance.get(`/api/bookings/${vm.$route.params.bookingId}/signed?signature=${vm.$route.query.signature}`)
          .then(data => {
            vm.$store.commit('setBooking', data.data.data)
            if (vm.$store.getters.campaign.campaign) {
              axiosInstance.put(data.data.data.signed_update.api, vm.$store.getters.campaign)
            }
          })
          .catch(() => {
            vm.$router.push({ name: 'notFound' })
          })
      }
    })
  },

  data () {
    return {
      form: {
        discountCode: ''
      },
      showEmergencyDiscount: false,
      showError: false,
      error: null,
      showMessage: false,
      message: null,
      showCouponField: false,
      validForm: true,
      loading: false,
      stripeIsComplete: false,
      listenForStripeCompletion: false
    }
  },

  mounted () {
    this.trackCheckoutStep5()
  },

  methods: {
    applyEmergencyDiscount () {
      if (this.form.emergencyDiscount) {
        this.form.discountCode = 'COVIDEMERGENCY1'
      }
      this.applyCoupon()
    },

    trackCheckoutStep5 () {
      var products = []
      if (this.$store.getters.booking.bikes) {
        this.$store.getters.booking.bikes.forEach(bike => {
          products.push({
            name: bike.service.name,
            id: bike.service_sku,
            price: bike.service.price,
            brand: 'havebike',
            category: 'Service',
            quantity: 1
          })
        })
        window.dataLayer.push({
          event: 'checkout',
          ecommerce: {
            checkout: {
              actionField: { step: 5, option: `${this.$store.getters.booking.bikes.length} bikes` },
              products: products
            }
          }
        })
      }
    },

    handlePayment () {
      this.$refs.form.validate()
      this.$refs.paymentForm.handlePayment()
    },

    handleCompletePayment (data) {
      const apiShowSignature = this.$store.getters.booking.signed_show.signature
      const bookingId = this.$store.getters.booking.id
      this.$store.commit('clearBookingForm')
      this.$router.push({
        name: 'completePage',
        params: {
          bookingId,
          apiShowSignature
        }
      })
    },

    applyCoupon () {
      this.$api.persist('post', {
        path: `bookings/${this.$store.getters.booking.id}/discount`,
        object: {
          code: this.form.discountCode,
          with: ['bikes.service']
        }
      })
        .then(data => {
          if (data.data.valid) {
            this.showMessage = true
            this.message = data.data.reason
            this.$store.commit('setBooking', data.data.booking)
            this.$refs.paymentForm.getPaymentIntent()
          } else {
            this.showError = true
            this.error = data.data.reason
          }
        })
        .catch(error => {
          this.showError = true
          this.error = error.message
        })
    }
  }
}
</script>
<style type="text/css">
.emergency-input input, .emergency-input label {
    color: #fb8c00!important;
}
</style>
